import moment from 'moment-timezone';

/**
 * @module beebee-base/lib/utility/dates
 * @description Date & time utilities.
 *
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — timezone-safe date/hour formatting, date-diff, and
 *              slot compaction, re-exported through the ../utility.js barrel
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      moment-timezone
 *   siblings:  sibling lib/utility/*.js modules aggregated by ../utility.js
 *              (the public `beebee-base/lib/utility` barrel import path)
 *   tests:     tests/unit/lib/dates-wave-test.js,
 *              tests/unit/lib/utility-test.js,
 *              tests/unit/lib/utility-extra-test.js,
 *              tests/unit/lib/utility-wave5-test.js,
 *              tests/unit/lib/application-helpers-test.js
 *   gotchas:   `safeTimeZone` exists specifically to dodge the moment-timezone
 *              getter trap — `.tz(undefined)` acts as a getter and returns
 *              undefined, so a chained `.format()` crashes for users with no
 *              `time_zone` set; always route an untrusted timezone through
 *              `safeTimeZone` before formatting.
 */


/**
 * Verifica se un timezone è valido
 * @param {string} timezone - Nome timezone
 * @returns {boolean}
 */
function isValidTimezone(timezone) {
  return moment.tz.zone(timezone) != null;
}

/**
 * Returns a valid IANA timezone: the given one when valid, otherwise the
 * browser timezone, otherwise 'Europe/Rome'.
 * Guards the moment-timezone getter trap: `.tz(undefined)` acts as a getter
 * and returns undefined, so any chained `.format()` crashes (e.g. users
 * without `time_zone` set on their profile).
 * @param {string|null|undefined} time_zone - Candidate timezone
 * @returns {string} Valid IANA timezone name
 */
function safeTimeZone(time_zone) {
  if (time_zone && time_zone !== 'auto' && moment.tz.zone(time_zone)) {
    return time_zone;
  }
  return moment.tz.guess() || 'Europe/Rome';
}

/**
 * Formatta una data con timezone
 * @param {Date|string} date - Data da formattare
 * @param {string} time_zone - Timezone (o 'auto' per rilevamento automatico)
 * @param {string} format - Formato output (o 'optimize'/'optimizeDay')
 * @returns {string|undefined}
 */
function formatDate(date, time_zone, format = 'DD/MM/YYYY') {
  if (!date) {
    return;
  }
  const tz = time_zone === 'auto' || !time_zone ? moment.tz.guess() : time_zone;
  const momentDate = moment.tz(date, tz);
  if (format === 'optimize') {
    return _optimizeDate(momentDate);
  }
  if (format === 'optimizeDay') {
    return _optimizeDate(momentDate, 'day');
  }
  return momentDate.format(format);
}

/**
 * Ottimizza il formato data basandosi sulla vicinanza temporale
 * @private
 * @param {moment.Moment} date - Data moment
 * @param {string} optimize - Precisione ottimizzazione ('minutes' o 'day')
 * @returns {string}
 */
function _optimizeDate(date, optimize = 'minutes') {
  const today = moment().startOf('day');
  const yesterday = moment(today).subtract(1, 'day');
  const tomorrow = moment(today).add(1, 'day');

  // Ieri, oggi o domani: usa calendario
  if (date.isSame(yesterday, optimize) || date.isSame(today, optimize) || date.isSame(tomorrow, optimize)) {
    return date.calendar().split(' ')[0];
  }

  // Stesso giorno: mostra solo ora
  if (date.isSame(today, 'day')) {
    return date.format('HH:mm');
  }

  // Stesso anno: giorno e mese
  if (date.isSame(today, 'year')) {
    return date.format('D MMM');
  }

  // Altro: data completa
  return date.format('DD/MM/YYYY');
}

/**
 * Formatta l'ora con timezone
 * @param {Date|string|moment.Moment} date - Data/ora da formattare
 * @param {string} time_zone - Timezone
 * @param {string} format - Formato output
 * @returns {string|undefined}
 */
function formatHour(date, time_zone = 'Europe/Rome', format = 'HH:mm') {
  if (!date) {
    return;
  }
  const tz = time_zone === 'auto' || !time_zone ? moment.tz.guess() : time_zone;
  return moment.isMoment(date) ? date.tz(tz).format(format) : moment.tz(date, tz).format(format);
}

/**
 * Calcola la differenza tra due date in ore e minuti
 * @param {Date|string} first_date - Prima data
 * @param {Date|string} second_date - Seconda data
 * @returns {string|undefined} Differenza formattata (es. "2 h 30 m")
 */
function dateDiff(first_date, second_date) {
  if (!first_date || !second_date) {
    return;
  }
  const totalSeconds = moment(new Date(second_date)).diff(first_date, 'second');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const hoursPart = hours > 0 ? `${hours} h ` : '';
  const minutesPart = minutes ? `${minutes} m` : '';
  return hoursPart + minutesPart;
}
function compattaSlot(slots, SlotElement, minutesToAdd = 30) {
  if (!Array.isArray(slots)) {
    return false;
  }
  const newSlots = [];
  for (let i = 0; i < slots.length; i++) {
    newSlots[i] = new SlotElement(slots[i].start_date, slots[i].end_date, slots[i].user_id, slots[i].event_step, slots[i].marked_timespan, slots[i].css);
  }
  const slotsSelectedSorted = newSlots.slice().sort(function (a, b) {
    if (moment(a.start_date).isAfter(b.start_date)) {
      return 1;
    } else if (moment(a.start_date).isSame(b.start_date)) {
      return 0;
    }
    return -1;
  });
  for (let i = 0; i < slotsSelectedSorted.length - 1; i++) {
    // Se uguale lo cancello in quanto doppio
    if (moment(slotsSelectedSorted[i].start_date).isSame(slotsSelectedSorted[i + 1].start_date)) {
      slotsSelectedSorted.splice(i, 1);
      i--;
    }

    // Se la fine è uguale all'inizio del successivo lo unisco
    else if (moment(slotsSelectedSorted[i].end_date).isSameOrAfter(slotsSelectedSorted[i + 1].start_date)) {
      slotsSelectedSorted[i + 1].start_date = slotsSelectedSorted[i].start_date;
      slotsSelectedSorted[i + 1].event_step += minutesToAdd;
      slotsSelectedSorted.splice(i, 1);
      i--;
    }
  }
  return slotsSelectedSorted;
}

export { compattaSlot, dateDiff, formatDate, formatHour, isValidTimezone, safeTimeZone };
//# sourceMappingURL=dates.js.map
